import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ClipboardPen, Loader2, Send, ArrowLeft, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { API, PROGRAM_OPTIONS } from "@/constants/site";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const EMPTY = {
  parent_name: "", child_name: "", child_age: "", phone: "",
  program: "", source: "Walk-in", fee_estimate: "", message: "",
};

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    {children}
  </label>
);

const inputCls =
  "rounded-2xl border-2 border-slate-200 bg-white px-4 py-3 h-auto text-base focus-visible:border-brand-sky focus-visible:ring-brand-sky/20 transition-colors";

export default function AdminDesk() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const token = sessionStorage.getItem("bca_admin_token");

  useEffect(() => {
    if (!token) navigate("/admin");
  }, [token, navigate]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.program) {
      toast.error("Please select a program.");
      return;
    }
    setSending(true);
    try {
      await axios.post(
        `${API}/admin/leads`,
        { ...form, email: null, fee_estimate: form.fee_estimate || null },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Logged! Fee estimate for ${form.child_name}: ${form.fee_estimate || "not quoted"}`);
      setForm(EMPTY);
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem("bca_admin_token");
        navigate("/admin");
      } else {
        toast.error("Could not save. Please try again.");
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="admin-desk-page" className="mx-auto max-w-3xl px-6 pb-24 pt-36">
      <Link to="/admin/leads" data-testid="desk-back-link" className="inline-flex items-center gap-2 text-sm font-bold text-brand-deepsky hover:underline">
        <ArrowLeft className="h-4 w-4" /> Back to all enquiries
      </Link>

      <div className="mt-6 flex items-center gap-4">
        <span className="grid h-14 w-14 place-items-center rounded-2xl bg-brand-sun/25 text-amber-600">
          <ClipboardPen className="h-7 w-7" />
        </span>
        <div>
          <h1 className="font-display text-4xl font-semibold tracking-tight">Reception Desk</h1>
          <p className="text-sm text-slate-500">Log walk-in & phone enquiries, and note the fee estimate quoted to parents.</p>
        </div>
      </div>

      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="desk-enquiry-form"
        className="mt-10 rounded-[2.5rem] border border-amber-200/50 bg-white p-7 md:p-10 shadow-[0_25px_60px_rgba(2,132,199,0.1)]"
      >
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Parent Name *">
            <Input required data-testid="desk-parent-name" value={form.parent_name} onChange={set("parent_name")} placeholder="e.g. Sunita Sharma" className={inputCls} />
          </Field>
          <Field label="Child Name *">
            <Input required data-testid="desk-child-name" value={form.child_name} onChange={set("child_name")} placeholder="e.g. Aarav" className={inputCls} />
          </Field>
          <Field label="Child Age *">
            <Input required data-testid="desk-child-age" value={form.child_age} onChange={set("child_age")} placeholder="e.g. 3.5 years" className={inputCls} />
          </Field>
          <Field label="Phone *">
            <Input required type="tel" data-testid="desk-phone" value={form.phone} onChange={set("phone")} placeholder="10-digit mobile number" className={inputCls} />
          </Field>
          <Field label="Program *">
            <div className="relative">
              <select required data-testid="desk-program" value={form.program} onChange={set("program")} className={`${inputCls} w-full appearance-none pr-10 ${form.program ? "text-ink" : "text-slate-400"}`}>
                <option value="" disabled>Select a program</option>
                {PROGRAM_OPTIONS.map((p) => (
                  <option key={p} value={p} className="text-ink">{p}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </Field>
          <Field label="Source *">
            <div className="relative">
              <select required data-testid="desk-source" value={form.source} onChange={set("source")} className={`${inputCls} w-full appearance-none pr-10`}>
                {["Walk-in", "Phone Call", "Referral", "Other"].map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </Field>
          <div className="sm:col-span-2">
            <Field label="Fee Estimate Quoted (₹)">
              <Input data-testid="desk-fee-estimate" value={form.fee_estimate} onChange={set("fee_estimate")} placeholder="e.g. ₹1,200/month + ₹3,000 admission" className={inputCls} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Notes">
              <Textarea data-testid="desk-notes" value={form.message} onChange={set("message")} placeholder="Visit planned, sibling already enrolled, call back after 5 PM..." rows={3} className={inputCls} />
            </Field>
          </div>
        </div>

        <motion.button
          type="submit"
          disabled={sending}
          data-testid="desk-submit-btn"
          whileTap={{ scale: 0.97 }}
          className="mt-8 inline-flex min-h-[52px] w-full sm:w-auto items-center justify-center gap-2 rounded-full bg-brand-sun px-10 font-bold text-ink shadow-[0_10px_30px_rgba(251,191,36,0.4)] transition-transform duration-300 hover:scale-105 disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-4 w-4" />}
          {sending ? "Saving..." : "Log Enquiry"}
        </motion.button>
      </motion.form>
    </main>
  );
}
